/**
 * Which key does what, and remembering it between sessions.
 *
 * The original writes its bindings straight back into `football.config` the moment they
 * change (`hid/keyboard.cpp:37`). There is no config file in a browser, so this keeps them in
 * the browser's own storage under one key and reloads them at startup. A binding that names a
 * key no longer on the layout is harmless: it simply never fires.
 */

export type BindableAction =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'shortPass'
  | 'longPass'
  | 'highPass'
  | 'shot'
  | 'sprint'
  | 'dribble'
  | 'switch'
  | 'special'
  | 'teamPressure'
  | 'keeperRush'
  | 'shirtPull'
  | 'flyingKick'
  | 'punch'
  | 'allOut'
  | 'feint'
  | 'shield'
  | 'chip';

/** Label shown on the settings screen, in the order it is listed there. */
export const BINDABLE_ACTIONS: readonly { action: BindableAction; label: string }[] = [
  { action: 'up', label: 'MOVE UP' },
  { action: 'down', label: 'MOVE DOWN' },
  { action: 'left', label: 'MOVE LEFT' },
  { action: 'right', label: 'MOVE RIGHT' },
  { action: 'shortPass', label: 'SHORT PASS' },
  { action: 'longPass', label: 'THROUGH PASS' },
  { action: 'highPass', label: 'HIGH PASS / SLIDE' },
  { action: 'shot', label: 'SHOT' },
  { action: 'sprint', label: 'SPRINT' },
  { action: 'dribble', label: 'DRIBBLE' },
  { action: 'switch', label: 'SWITCH PLAYER' },
  { action: 'special', label: 'LONG TOUCH' },
  { action: 'teamPressure', label: 'TEAM PRESSURE' },
  { action: 'keeperRush', label: 'KEEPER RUSH' },
  { action: 'shirtPull', label: 'PULL HIS SHIRT' },
  { action: 'flyingKick', label: 'FLYING KICK' },
  { action: 'punch', label: 'PUNCH HIM' },
  { action: 'feint', label: 'BODY SWERVE' },
  { action: 'shield', label: 'SHIELD THE BALL' },
  { action: 'chip', label: 'CHIP THE KEEPER' },
  { action: 'allOut', label: 'ALL OUT' },
];

export type KeyBindings = Record<BindableAction, string>;

export const DEFAULT_BINDINGS: KeyBindings = {
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  shortPass: 'KeyS',
  longPass: 'KeyW',
  highPass: 'KeyA',
  shot: 'KeyD',
  sprint: 'KeyE',
  dribble: 'KeyC',
  switch: 'KeyQ',
  special: 'KeyZ',
  teamPressure: 'KeyF',
  keeperRush: 'KeyR',
  // Ours, like the two above: the original has no such orders and reads its own bindings from
  // a config file we do not have. X, V and B sit under the same hand as the rest, in the order
  // the offence gets worse; G is the third order to the rest of the side, beside F and R.
  shirtPull: 'KeyX',
  flyingKick: 'KeyV',
  punch: 'KeyB',
  allOut: 'KeyG',
  // The only one of ours that is football rather than an order or an offence, so it sits away
  // from the three offences on X, V and B and next to the movement keys' own hand.
  feint: 'KeyT',
  shield: 'KeyY',
  chip: 'KeyU',
};

const STORAGE_KEY = 'gf.keyBindings.v1';

let current: KeyBindings = { ...DEFAULT_BINDINGS };
const listeners = new Set<(bindings: KeyBindings) => void>();

/** Restore whatever was saved. Unknown or malformed entries fall back to the default. */
export function loadBindings(): KeyBindings {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<KeyBindings>;
      const merged = { ...DEFAULT_BINDINGS };
      for (const { action } of BINDABLE_ACTIONS) {
        const value = parsed[action];
        // An empty string is a deliberate unbind — it happens whenever a key is taken from
        // one action and given to another — and has to survive a reload. Treating it as
        // "missing" and restoring the default puts two actions back on the same key, where
        // only one of them would ever fire.
        if (typeof value === 'string') merged[action] = value;
      }
      current = merged;
    }
  } catch {
    // Storage can be unavailable or corrupt; the defaults are always a valid answer.
    current = { ...DEFAULT_BINDINGS };
  }
  return current;
}

export function getBindings(): KeyBindings {
  return current;
}

/**
 * Point one action at a key. Any other action already using that key is unbound, since two
 * actions on one key means the second one silently never happens.
 */
export function setBinding(action: BindableAction, code: string): KeyBindings {
  const next: KeyBindings = { ...current };
  for (const other of BINDABLE_ACTIONS) {
    if (other.action !== action && next[other.action] === code) next[other.action] = '';
  }
  next[action] = code;
  return commit(next);
}

export function resetBindings(): KeyBindings {
  return commit({ ...DEFAULT_BINDINGS });
}

function commit(next: KeyBindings): KeyBindings {
  current = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Not being able to save is not a reason to refuse the change for this session.
  }
  for (const listener of listeners) listener(current);
  return current;
}

/** Called whenever a binding changes, so a running match picks it up without a restart. */
export function onBindingsChanged(listener: (bindings: KeyBindings) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** How a key code should read on screen: "ARROW UP", "S", "F1". */
export function keyLabel(code: string): string {
  if (!code) return '—';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return `ARROW ${code.slice(5).toUpperCase()}`;
  return code.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
}
